<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-content">
      <SettingsNav />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Customer Status Settings -->
        <div class="bg-white dark:bg-[#0c1427] p-5 rounded-md shadow-md flex flex-col min-h-[450px]">
          <h6 class="text-black dark:text-white font-semibold mb-6 text-center text-xl">{{ directT('settings.customersSettings.statusManagement.title') }}</h6>
          
          <div class="mb-5 flex-grow">
            <div v-if="statusList.length === 0" class="text-gray-500 italic">
              {{ directT('settings.customersSettings.statusManagement.noStatuses') }}
            </div>
            
            <ul class="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              <li 
                v-for="(status, index) in statusList" 
                :key="index"
                class="flex items-center justify-between bg-white dark:bg-[#0c1427] p-3 rounded border border-gray-200 dark:border-[#1c2b50]"
              >
                <div class="flex items-center">
                  <span 
                    class="h-3 w-3 rounded-full inline-block mr-2" 
                    :style="{ backgroundColor: status.color }"
                  ></span>
                  <span class="text-black dark:text-white">{{ status.name }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <button 
                    @click="editStatus(index)" 
                    class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <i class="material-symbols-outlined !text-md">edit</i>
                  </button>
                  <button 
                    @click="confirmDeleteStatus(index)" 
                    class="text-red-500 hover:text-red-700"
                  >
                    <i class="material-symbols-outlined !text-md">delete</i>
                  </button>
                </div>
              </li>
            </ul>
          </div>
          
          <div class="flex justify-between flex-nowrap gap-2 w-full mt-auto">
            <button 
              @click="addNewStatus" 
              class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md flex items-center justify-center whitespace-nowrap min-w-[120px]"
            >
              <span>{{ directT('settings.customersSettings.statusManagement.addStatus') }}</span>
            </button>
            
            <button 
              @click="saveStatusSettings" 
              class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md whitespace-nowrap min-w-[120px] flex items-center justify-center"
              :disabled="saving"
            >
              {{ saving ? directT('settings.customersSettings.statusManagement.saving') : directT('settings.customersSettings.statusManagement.saveChanges') }}
            </button>
          </div>
          <p class="text-sm text-red-500 mt-2 text-center">
            {{ directT('settings.customersSettings.statusManagement.important') }}
          </p>
        </div>
        
        <!-- Custom Fields Settings -->
        <div>
          <CustomFields :organizationId="organizationId" />
        </div>
      </div>
    </div>
    
    <!-- Status Modal -->
    <div 
      v-if="showStatusModal" 
      class="fixed inset-0 flex items-center justify-center z-50"
    >
      <div class="fixed inset-0 bg-black opacity-50" @click="closeStatusModal"></div>
      <div class="bg-white dark:bg-[#0c1427] p-6 rounded-md shadow-lg w-full max-w-md relative z-10">
        <h5 class="text-xl font-bold mb-4 text-black dark:text-white">
          {{ editingStatusIndex === -1 ? directT('settings.customersSettings.statusManagement.addStatusTitle') : directT('settings.customersSettings.statusManagement.editStatusTitle') }}
        </h5>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ directT('settings.customersSettings.statusManagement.statusName') }}
          </label>
          <input
            v-model="editingStatus.name"
            type="text"
            class="h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-4 block w-full outline-0 focus:border-primary-500"
            :placeholder="directT('settings.customersSettings.statusManagement.statusNamePlaceholder')"
          />
        </div>
        
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ directT('settings.customersSettings.statusManagement.statusColor') }}
          </label>
          <input
            v-model="editingStatus.color"
            type="color"
            class="h-[45px] rounded-md border border-gray-200 dark:border-[#172036] p-1 block w-full outline-0 focus:border-primary-500"
          />
        </div>
        
        <div class="flex justify-end space-x-3">
          <button
            @click="closeStatusModal"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-50 dark:hover:bg-[#172036]"
          >
            {{ directT('settings.customersSettings.statusManagement.cancel') }}
          </button>
          <button
            @click="saveStatus"
            class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md"
          >
            {{ editingStatusIndex === -1 ? directT('settings.customersSettings.statusManagement.add') : directT('settings.customersSettings.statusManagement.update') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteModal" 
      class="fixed inset-0 flex items-center justify-center z-50"
    >
      <div class="fixed inset-0 bg-black opacity-50" @click="closeDeleteModal"></div>
      <div class="bg-white dark:bg-[#0c1427] p-6 rounded-md shadow-lg w-full max-w-md relative z-10">
        <h5 class="text-xl font-bold mb-4 text-black dark:text-white">{{ directT('settings.customersSettings.statusManagement.confirmDeleteTitle') }}</h5>
        <p class="mb-6 text-gray-700 dark:text-gray-300">
          {{ directT('settings.customersSettings.statusManagement.deleteConfirmation') }}
        </p>
        
        <div class="flex justify-end space-x-3">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-50 dark:hover:bg-[#172036]"
          >
            {{ directT('settings.customersSettings.statusManagement.cancel') }}
          </button>
          <button
            @click="deleteStatus"
            class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
          >
            {{ directT('settings.customersSettings.statusManagement.delete') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Toast Notification -->
    <div 
      v-if="toastMessage" 
      class="fixed bottom-4 right-4 left-4 md:left-auto md:max-w-md p-4 rounded-md shadow-lg transform transition-transform duration-300 z-50 flex items-center justify-between"
      :class="{ 
        'bg-green-600 text-white': toastType === 'success', 
        'bg-red-600 text-white': toastType === 'error', 
        'bg-blue-600 text-white': toastType === 'info' 
      }"
    >
      <span>{{ toastMessage }}</span>
      <button @click="closeToast" class="ml-4 text-white hover:text-gray-300">×</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import SettingsNav from "../SettingsNav.vue";
import { supabase } from "@/lib/supabaseClient";
import { createToast } from "@/utils/toast";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import CustomFields from "./CustomFields/index.vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

interface CustomerStatusRecord {
  id: string;
  statusname: string;
  color: string;
  organizationid: string;
  created_at?: string;
  updated_at?: string;
}

interface Status {
  id?: string;
  name: string;
  color: string;
  organizationid?: string;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "CustomersSettings",
  components: {
    SettingsNav,
    CustomFields,
  },
  setup() {
    const statusList = ref<Status[]>([]);
    const saving = ref(false);
    const organizationId = ref<string | null>(null);
    
    // Status modal
    const showStatusModal = ref(false);
    const editingStatus = ref<Status>({ name: '', color: '#605DFF' });
    const editingStatusIndex = ref(-1);
    
    // Delete confirmation modal
    const showDeleteModal = ref(false);
    const deleteIndex = ref<number>(-1);
    
    // Toast notification
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info'>('info');
    const toastTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
    
    // i18n setup
    const { locale } = useI18n();
    
    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    // Fetch settings on component mount
    onMounted(async () => {
      try {
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          createToast(directT('settings.customersSettings.toastMessages.missingOrg'), 'error');
          return;
        }
        
        await fetchCustomerSettings();
      } catch (error) {
        console.error('Error loading customer settings:', error);
        createToast(directT('settings.customersSettings.toastMessages.loadingError'), 'error');
      }
    });
    
    // Fetch customer settings from the database
    const fetchCustomerSettings = async () => {
      try {
        // Fetch customer statuses from customercustomstatus table
        const { data: statusData, error: statusError } = await supabase
          .from('customercustomstatus')
          .select('*')
          .eq('organizationid', organizationId.value);
        
        if (statusError) throw statusError;
        
        // Map the database records to the Status interface
        statusList.value = (statusData || []).map((item: any) => ({
          id: item.id as string,
          name: item.statusname as string,
          color: (item.color as string) || '#605DFF', // Default color if not provided
          organizationid: item.organizationid as string
        }));
        
        // If no statuses exist, create default ones
        if (statusList.value.length === 0) {
          await createDefaultStatuses();
        }
      } catch (error) {
        console.error('Error fetching customer settings:', error);
        showToast(directT('settings.customersSettings.toastMessages.fetchError'), 'error');
      }
    };
    
    // Create default statuses if none exist
    const createDefaultStatuses = async () => {
      try {
        const defaultStatuses: Status[] = [
          { name: 'New', color: '#4CAF50', organizationid: organizationId.value as string },
          { name: 'Active', color: '#2196F3', organizationid: organizationId.value as string },
          { name: 'Inactive', color: '#FF9800', organizationid: organizationId.value as string },
          { name: 'Former', color: '#F44336', organizationid: organizationId.value as string },
        ];
        
        // Add default statuses to the database
        const { error } = await supabase
          .from('customercustomstatus')
          .insert(defaultStatuses.map(status => ({
            statusname: status.name,
            color: status.color,
            organizationid: status.organizationid
          })));
        
        if (error) throw error;
        
        // Refresh the status list
        await fetchCustomerSettings();
      } catch (error) {
        console.error('Error creating default statuses:', error);
        showToast(directT('settings.customersSettings.toastMessages.defaultError'), 'error');
      }
    };
    
    // Open the status modal for adding a new status
    const addNewStatus = () => {
      editingStatus.value = { name: '', color: '#605DFF' };
      editingStatusIndex.value = -1;
      showStatusModal.value = true;
    };
    
    // Open the status modal for editing an existing status
    const editStatus = (index: number) => {
      const status = statusList.value[index];
      editingStatus.value = { ...status };
      editingStatusIndex.value = index;
      showStatusModal.value = true;
    };
    
    // Save the current status being edited
    const saveStatus = () => {
      // Validate inputs
      if (!editingStatus.value.name.trim()) {
        showToast(directT('settings.customersSettings.toastMessages.nameRequired'), 'error');
        return;
      }
      
      // Update the statusList with the new or edited status
      if (editingStatusIndex.value === -1) {
        // Add new status
        statusList.value.push({ ...editingStatus.value });
      } else {
        // Update existing status
        statusList.value[editingStatusIndex.value] = { ...editingStatus.value };
      }
      
      // Close the modal
      closeStatusModal();
    };
    
    // Close the status modal
    const closeStatusModal = () => {
      showStatusModal.value = false;
      editingStatus.value = { name: '', color: '#605DFF' };
      editingStatusIndex.value = -1;
    };
    
    // Confirm deletion of a status
    const confirmDeleteStatus = (index: number) => {
      deleteIndex.value = index;
      showDeleteModal.value = true;
    };
    
    // Delete a status
    const deleteStatus = () => {
      if (deleteIndex.value >= 0 && deleteIndex.value < statusList.value.length) {
        statusList.value.splice(deleteIndex.value, 1);
      }
      closeDeleteModal();
    };
    
    // Close the delete modal
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      deleteIndex.value = -1;
    };
    
    // Save all status settings to the database
    const saveStatusSettings = async () => {
      saving.value = true;
      console.log('Saving status settings to database:', statusList.value);
      
      try {
        // Get existing statuses from the database
        const { data: existingStatusData, error: fetchError } = await supabase
          .from('customercustomstatus')
          .select('id')
          .eq('organizationid', organizationId.value);
        
        if (fetchError) throw fetchError;
        
        // Create a set of existing IDs
        const existingIds = new Set((existingStatusData || []).map((item: any) => item.id));
        
        // Identify statuses to add, update, or delete
        const statusesToUpdate: any[] = [];
        const statusesToAdd: any[] = [];
        
        // Process statuses in the UI
        statusList.value.forEach((status) => {
          const dbStatus = {
            statusname: status.name,
            color: status.color,
            organizationid: organizationId.value as string
          };
          
          if (status.id && existingIds.has(status.id)) {
            // Status exists, update it
            statusesToUpdate.push({
              ...dbStatus,
              id: status.id
            });
            // Remove from the set of existing IDs to track what's left to delete
            existingIds.delete(status.id);
          } else {
            // Status is new, add it
            statusesToAdd.push(dbStatus);
          }
        });
        
        // Update existing statuses
        if (statusesToUpdate.length > 0) {
          const { error: updateError } = await supabase
            .from('customercustomstatus')
            .upsert(statusesToUpdate);
          
          if (updateError) throw updateError;
        }
        
        // Add new statuses
        if (statusesToAdd.length > 0) {
          const { error: addError } = await supabase
            .from('customercustomstatus')
            .insert(statusesToAdd);
          
          if (addError) throw addError;
        }
        
        // Delete removed statuses
        if (existingIds.size > 0) {
          const { error: deleteError } = await supabase
            .from('customercustomstatus')
            .delete()
            .in('id', Array.from(existingIds));
          
          if (deleteError) throw deleteError;
        }
        
        // Refresh the status list
        await fetchCustomerSettings();
        
        showToast(directT('settings.customersSettings.toastMessages.saveSuccess'), 'success');
      } catch (error) {
        console.error('Error saving customer settings:', error);
        showToast(directT('settings.customersSettings.toastMessages.saveError'), 'error');
      } finally {
        saving.value = false;
      }
    };
    
    // Show a toast message
    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      toastMessage.value = message;
      toastType.value = type;
      
      // Clear existing timeout
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
      }
      
      // Auto-hide the toast after 3 seconds
      toastTimeout.value = setTimeout(() => {
        closeToast();
      }, 3000);
    };
    
    // Close the toast
    const closeToast = () => {
      toastMessage.value = '';
      
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
        toastTimeout.value = null;
      }
    };
    
    return {
      statusList,
      saving,
      organizationId,
      showStatusModal,
      editingStatus,
      editingStatusIndex,
      showDeleteModal,
      deleteIndex,
      toastMessage,
      toastType,
      directT,
      addNewStatus,
      editStatus,
      saveStatus,
      closeStatusModal,
      confirmDeleteStatus,
      deleteStatus,
      closeDeleteModal,
      saveStatusSettings,
      closeToast
    };
  }
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #0c1427;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #555;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #777;
}
</style> 